import * as React from "react"
import { useSearchParams } from "react-router"
import { useQuery } from "@tanstack/react-query"

import { api } from "@/lib/api"
import { SectionCard } from "@/components/console/SectionCard"
import { EvidenceCard } from "@/components/console/EvidenceCard"
import { PageSkeleton, FetchError } from "@/components/console/Fallbacks"
import { Input } from "@/components/ui/input"

export function Search({ token }: { token: string }) {
  const [params, setParams] = useSearchParams()
  const q = params.get("q") ?? ""
  const inputRef = React.useRef<HTMLInputElement>(null)

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = inputRef.current?.value.trim() ?? ""
    if (trimmed) setParams({ q: trimmed })
  }

  const results = useQuery({
    queryKey: ["search", q, token],
    queryFn: () => api.search(token, q),
    enabled: q.length > 0,
  })

  const searchForm = (
    <form onSubmit={onSearch}>
      <Input
        ref={inputRef}
        // key remounts the (uncontrolled) input when the URL's q changes --
        // e.g. a direct link with ?q=... or browser back/forward -- so its
        // defaultValue picks up the new query without fighting the user's
        // in-progress typing on every keystroke.
        key={q}
        placeholder="Ask the brain…"
        defaultValue={q}
      />
    </form>
  )

  if (!q) {
    return (
      <div className="space-y-3">
        {searchForm}
        <p className="font-mono text-xs text-muted-foreground">Type a query above.</p>
      </div>
    )
  }
  if (results.isPending) {
    return (
      <div className="space-y-3">
        {searchForm}
        <PageSkeleton />
      </div>
    )
  }
  if (results.isError) {
    return (
      <div className="space-y-3">
        {searchForm}
        <FetchError error={results.error} retry={() => results.refetch()} />
      </div>
    )
  }

  const data = results.data
  // A source appears in exactly one of `results`/`errors`, never both (see
  // Services.search) — union the two so a fully-failed source still renders
  // its own SectionCard with an inline error instead of vanishing entirely.
  const sources = [...new Set([...Object.keys(data.results), ...Object.keys(data.errors)])]

  return (
    <div className="space-y-3">
      {searchForm}
      {sources.map((source) => (
        <SectionCard key={source} title={source} error={data.errors[source] ?? null}>
          {Object.hasOwn(data.results, source) ? (
            <EvidenceCard payload={data.results[source]} />
          ) : null}
        </SectionCard>
      ))}
      {sources.length === 0 ? (
        <p className="font-mono text-xs text-muted-foreground">No results.</p>
      ) : null}
    </div>
  )
}
